import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';

const S = {
  burgundy: '#800020', burgundy2: '#722f37',
  textPrimary: '#1e293b', textSecondary: '#475569', textMuted: '#64748b',
  borderLight: '#e2e8f0', bgCard: '#f8fafc', bgHover: '#f1f5f9',
};

const STEP_CONFIG = [
  {
    id: 1,
    tPrefix: 's1',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#800020" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/>
      </svg>
    ),
    cta: null,
  },
  {
    id: 2,
    tPrefix: 's2',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#800020" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
    cta: { path: '/dashboard' },
  },
  {
    id: 3,
    tPrefix: 's3',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#800020" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
    cta: { path: '/claims' },
  },
  {
    id: 4,
    tPrefix: 's4',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#800020" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
      </svg>
    ),
    cta: { path: '/claims/new' },
  },
  {
    id: 5,
    tPrefix: 's5',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#800020" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
    cta: null,
  },
  {
    id: 6,
    tPrefix: 's6',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#800020" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    ),
    cta: { path: '/workflow-rules' },
  },
  {
    id: 7,
    tPrefix: 's7',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#800020" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    cta: null,
  },
  {
    id: 8,
    tPrefix: 's8',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#800020" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    ),
    cta: { path: '/dsar-requests' },
  },
  {
    id: 9,
    tPrefix: 's9',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#800020" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
      </svg>
    ),
    cta: { path: '/api-keys' },
  },
  {
    id: 10,
    tPrefix: 's10',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    ),
    cta: { path: '/dashboard' },
  },
];

const DemoScreen = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [step, setStep] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);

  useEffect(() => {
    if (!autoPlay) return;
    if (step >= STEP_CONFIG.length - 1) { setAutoPlay(false); return; }
    const timer = setTimeout(() => setStep(s => s + 1), 4000);
    return () => clearTimeout(timer);
  }, [autoPlay, step]);

  const current = STEP_CONFIG[step];
  const title = t(`screens.demo.${current.tPrefix}Title`);
  const subtitle = t(`screens.demo.${current.tPrefix}Sub`);
  const description = t(`screens.demo.${current.tPrefix}Desc`);
  const highlight = t(`screens.demo.${current.tPrefix}Highlight`);
  const ctaLabel = current.cta ? t(`screens.demo.${current.tPrefix}Cta`) : null;

  return (
    <AppLayout>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'linear-gradient(135deg, #fdf2f4 0%, #f8fafc 100%)' }}>

        {/* Header */}
        <header style={{ padding: '20px 40px', borderBottom: `1px solid ${S.borderLight}`, background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(20px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 4 }}>{t('screens.demo.eyebrow')}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: S.textPrimary }}>{t('screens.demo.title')}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button onClick={() => setAutoPlay(a => !a)}
              style={{ padding: '8px 16px', background: autoPlay ? `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})` : '#fff', color: autoPlay ? '#fff' : S.textSecondary, border: `1px solid ${autoPlay ? S.burgundy : S.borderLight}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {autoPlay ? t('screens.demo.pause') : t('screens.demo.autoPlay')}
            </button>
            <button onClick={() => history.push('/dashboard')}
              style={{ padding: '8px 16px', background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, fontWeight: 500, color: S.textSecondary, cursor: 'pointer' }}>
              {t('screens.demo.exit')}
            </button>
          </div>
        </header>

        {/* Step indicator */}
        <div style={{ padding: '16px 40px', background: 'rgba(255,255,255,0.5)', borderBottom: `1px solid ${S.borderLight}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {STEP_CONFIG.map((s, i) => (
              <button key={s.id} onClick={() => setStep(i)}
                style={{ height: 4, flex: 1, borderRadius: 2, border: 'none', cursor: 'pointer', background: i === step ? S.burgundy : i < step ? '#800020aa' : S.borderLight, transition: 'all 0.2s' }} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
            <span style={{ fontSize: 12, color: S.textMuted }}>{t('screens.demo.stepOfTotal', { current: step + 1, total: STEP_CONFIG.length })}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: S.burgundy }}>{highlight}</span>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
          <div style={{ maxWidth: 720, width: '100%', textAlign: 'center' }}>

            {/* Icon */}
            <div style={{ width: 96, height: 96, borderRadius: 24, background: '#fff', border: `2px solid ${S.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 32px', boxShadow: '0 4px 24px rgba(128,0,32,0.08)' }}>
              {current.icon}
            </div>

            {/* Text */}
            <div style={{ fontSize: 13, fontWeight: 600, color: S.burgundy, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>{subtitle}</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: S.textPrimary, marginBottom: 20, lineHeight: 1.2 }}>{title}</div>
            <div style={{ fontSize: 16, color: S.textSecondary, lineHeight: 1.7, maxWidth: 600, margin: '0 auto 40px' }}>{description}</div>

            {/* CTA */}
            {current.cta && (
              <button onClick={() => history.push(current.cta.path)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px', background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`, color: '#fff', border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 32, boxShadow: '0 4px 16px rgba(128,0,32,0.25)' }}>
                {ctaLabel}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div style={{ padding: '16px 40px', borderTop: `1px solid ${S.borderLight}`, background: 'rgba(255,255,255,0.8)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, fontSize: 13, fontWeight: 600, color: step === 0 ? S.textMuted : S.textSecondary, cursor: step === 0 ? 'default' : 'pointer', opacity: step === 0 ? 0.5 : 1 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            {t('screens.demo.previous')}
          </button>

          {/* Step dots */}
          <div style={{ display: 'flex', gap: 6 }}>
            {STEP_CONFIG.map((_, i) => (
              <button key={i} onClick={() => setStep(i)}
                style={{ width: i === step ? 24 : 8, height: 8, borderRadius: 4, border: 'none', cursor: 'pointer', background: i === step ? S.burgundy : i < step ? '#800020aa' : S.borderLight, transition: 'all 0.2s' }} />
            ))}
          </div>

          {step < STEP_CONFIG.length - 1 ? (
            <button onClick={() => setStep(s => s + 1)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`, color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {t('screens.demo.next')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </button>
          ) : (
            <button onClick={() => history.push('/dashboard')}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#15803d', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              {t('screens.demo.launch')}
            </button>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default DemoScreen;
